import type { Uuid } from "spacetimedb";
import { useState } from "react";

import { ActionCard } from "@/web/routes/_protected/smb/-dashboard/-ActionCard";
import { ExplorePropertiesDrawer } from "@/web/routes/_protected/smb/-dashboard/-actions/-ExplorePropertiesDrawer";
import type {
  SmbActionCoreProps,
  SmbActionDefinition,
} from "@/web/routes/_protected/smb/-dashboard/-types";

type ExplorePropertiesProps = {
  /** Underwritable entity the chosen properties get linked to. */
  underwritableEntity: Uuid;
};

function ExplorePropertiesAction({
  underwritableEntity,
  onSkip,
  onComplete,
}: ExplorePropertiesProps & SmbActionCoreProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <ActionCard category="negotiate">
        <ActionCard.Header>
          <ActionCard.Badge>Price</ActionCard.Badge>
          <ActionCard.DetailsTrigger />
        </ActionCard.Header>
        <ActionCard.Main>
          <ActionCard.Title>Explore properties you want to own</ActionCard.Title>
          <ActionCard.Description>
            Pick up to three locations or assets you want financing tied to — your own operating
            locations or properties on the market — so offers stay scoped to the right collateral.
          </ActionCard.Description>
        </ActionCard.Main>
        <ActionCard.Footer>
          <ActionCard.SkipButton onSkip={onSkip} />
          <ActionCard.PrimaryButton onClick={() => setOpen(true)}>Explore</ActionCard.PrimaryButton>
        </ActionCard.Footer>
        <ActionCard.Back title="Why we ask">
          <p>
            Fundraising terms depend on which properties back the deal. Choosing up front keeps
            diligence, projections, and lender conversations aligned with those assets.
          </p>
        </ActionCard.Back>
      </ActionCard>

      <ExplorePropertiesDrawer
        open={open}
        underwritableEntity={underwritableEntity}
        onOpenChange={setOpen}
        onComplete={onComplete}
      />
    </>
  );
}

export function createExplorePropertiesAction(
  props: ExplorePropertiesProps & { id?: string; canSkip?: boolean },
): SmbActionDefinition {
  const { id = "explore-properties", canSkip = true, ...rest } = props;
  return {
    id,
    category: "negotiate",
    canSkip,
    render: (core) => <ExplorePropertiesAction {...rest} {...core} />,
  };
}

// ── Engine rules ─────────────────────────────────────────────────────────────
import {
  CardRulesEngine,
  type CardRulesData,
} from "@/web/routes/_protected/smb/-dashboard/-CardRulesEngine";

// One-shot: shows while the UW entity has no linked property AND the user has
// *completed* the "confirm operating locations" step. "Completed" means either:
//   - they confirmed at least one discovered location, OR
//   - there was genuinely nothing to confirm (discovery surfaced no locations
//     and has settled into a terminal state) — this mirrors the confirm card's
//     empty "nothing to confirm, skip to the next step" state.
// Critically, when locations *were* discovered the user MUST confirm at least
// one before Explore unlocks: they can't run target-property selection until
// their operating locations are confirmed. Once they pick 1–3 target properties
// the card disappears. The per-property completed card lives in
// SelectPropertyCompletedRule.
//
// Hidden for invited brands (`explore-properties` is an organic onboarding
// card; invited owners start from a known target property instead).
const explorePropertiesWhen = (d: CardRulesData) => {
  if (!CardRulesEngine.hasNoProperty(d)) return false;
  const { confirmedCount, discoveredCount, jobStatus } = d.operatingLocations;
  // Confirmed at least one location → step complete.
  if (confirmedCount > 0) return true;
  // Otherwise only let Explore through when there was nothing to confirm:
  // no locations discovered AND discovery has terminally settled (matches
  // the confirm card's terminal-empty copy; `undefined` covers legacy brands
  // with no job and is treated as terminal there too). If locations were
  // discovered, hold Explore back until the user confirms one.
  const status = jobStatus?.tag;
  return (
    discoveredCount === 0 &&
    (status === undefined || status === "NoResults" || status === "Failed" || status === "TimedOut")
  );
};

export const explorePropertiesRule = CardRulesEngine.singletonRule({
  id: "explore-properties",
  priority: 100,
  when: CardRulesEngine.and(
    explorePropertiesWhen,
    CardRulesEngine.not(CardRulesEngine.businessStoryIs("Invited")),
  ),
  build: (d) => createExplorePropertiesAction({ underwritableEntity: d.underwritableEntityId }),
});
